import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { Badge } from '@/components/ui/Badge';
import { ptBR } from '@/i18n/pt-BR';
import { PROVIDER_PAPEL, type ApiKeyListItem } from '@/lib/api_keys';
import {
  Blueprint,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

function formatDate(iso: string | null): string {
  if (iso === null) return ptBR.apiKeys.itemLastUsedAtNever;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR');
}

/**
 * Rótulo do provider pelo catálogo do i18n. Enquanto havia um provider só, a
 * lista exibia o nome fixo — com o bureau cadastral (E74.F2) isso passaria a
 * mentir sobre metade das linhas. O fallback é neutro, nunca o código cru.
 */
function rotuloDoProvider(provider: string): string {
  const papeis: Record<string, string | undefined> = PROVIDER_PAPEL;
  const nomes: Record<string, string | undefined> = ptBR.apiKeys.addDialogProviderNames;
  const papel = papeis[provider];
  const rotulo = papel === undefined ? undefined : nomes[papel];
  return rotulo ?? ptBR.apiKeys.addDialogProviderDisplayName;
}

interface RowProps {
  item: ApiKeyListItem;
  onRotate(item: ApiKeyListItem): void;
  onRevoke(item: ApiKeyListItem): void;
  revoking: boolean;
}

function ApiKeyRow({ item, onRotate, onRevoke, revoking }: RowProps) {
  return (
    <TableRow data-testid={`api-key-row-${item.id}`} className="align-middle">
      <TableCell className="text-body-sm text-fg" data-provider={item.provider}>
        {rotuloDoProvider(item.provider)}
      </TableCell>
      <TableCell className="font-mono text-body-sm text-fg">••••{item.last4}</TableCell>
      <TableCell className="text-body-sm text-fg-muted">{formatDate(item.created_at)}</TableCell>
      <TableCell className="text-body-sm text-fg-muted">{formatDate(item.last_used_at)}</TableCell>
      <TableCell>
        <Badge variant="success" data-testid={`api-key-status-${item.id}`}>
          {ptBR.apiKeys.itemStatusActive}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => onRotate(item)}
            data-testid={`api-key-rotate-${item.id}`}
          >
            {ptBR.apiKeys.rotateCta}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => onRevoke(item)}
            disabled={revoking}
            data-testid={`api-key-revoke-${item.id}`}
          >
            {revoking ? ptBR.apiKeys.removing : ptBR.apiKeys.revokeCta}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

interface RevokeConfirmProps {
  item: ApiKeyListItem;
  onConfirm(): void;
  onCancel(): void;
  busy: boolean;
}

function RevokeConfirm({ item, onConfirm, onCancel, busy }: RevokeConfirmProps) {
  return (
    <ConfirmModal
      open
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
      title={ptBR.apiKeys.revokeConfirmTitle}
      description={`${ptBR.apiKeys.revokeConfirmHint} (••••${item.last4})`}
      confirmLabel={busy ? ptBR.apiKeys.removing : ptBR.apiKeys.revokeConfirmYes}
      cancelLabel={ptBR.apiKeys.revokeConfirmNo}
      variant="destructive"
      onConfirm={onConfirm}
      busy={busy}
    />
  );
}

export interface ApiKeysListViewProps {
  loading: boolean;
  loadError: string | null;
  items: ApiKeyListItem[] | null;
  revokeTarget: ApiKeyListItem | null;
  revokeBusyId: string | null;
  onRetry(): void;
  onRotateClick(item: ApiKeyListItem): void;
  onRevokeClick(item: ApiKeyListItem): void;
  onRevokeConfirm(): void;
  onRevokeCancel(): void;
}

export function ApiKeysListView({
  loading,
  loadError,
  items,
  revokeTarget,
  revokeBusyId,
  onRetry,
  onRotateClick,
  onRevokeClick,
  onRevokeConfirm,
  onRevokeCancel,
}: ApiKeysListViewProps) {
  if (loading) {
    return (
      <p className="mt-6 text-body-md text-fg-muted" data-testid="api-keys-loading">
        {ptBR.apiKeys.listLoading}
      </p>
    );
  }

  if (loadError !== null) {
    return (
      <div
        role="alert"
        data-testid="api-keys-load-error"
        className="mt-6 flex items-center justify-between border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
      >
        <span>{loadError}</span>
        <Button variant="secondary" type="button" onClick={onRetry}>
          {ptBR.apiKeys.listErrorRetry}
        </Button>
      </div>
    );
  }

  if (items === null || items.length === 0) {
    return (
      <Blueprint className="mt-sc-6 p-sc-6 text-center" data-testid="api-keys-empty">
        <p className="text-body-md font-semibold text-fg">{ptBR.apiKeys.listEmptyTitle}</p>
        <p className="mt-1 text-body-sm text-fg-muted">{ptBR.apiKeys.listEmptyHint}</p>
      </Blueprint>
    );
  }

  return (
    <Blueprint className="mt-sc-6" data-testid="api-keys-list">
      <div className="overflow-x-auto [contain:layout]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{ptBR.apiKeys.itemProviderLabel}</TableHead>
              <TableHead>{ptBR.apiKeys.itemLast4Label}</TableHead>
              <TableHead>{ptBR.apiKeys.itemCreatedAtLabel}</TableHead>
              <TableHead>{ptBR.apiKeys.itemLastUsedAtLabel}</TableHead>
              <TableHead>{ptBR.apiKeys.itemStatusLabel}</TableHead>
              <TableHead className="text-right">{ptBR.apiKeys.columnActions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <ApiKeyRow
                key={item.id}
                item={item}
                onRotate={onRotateClick}
                onRevoke={onRevokeClick}
                revoking={revokeBusyId === item.id}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {revokeTarget !== null ? (
        <RevokeConfirm
          item={revokeTarget}
          onConfirm={onRevokeConfirm}
          onCancel={onRevokeCancel}
          busy={revokeBusyId === revokeTarget.id}
        />
      ) : null}
    </Blueprint>
  );
}
